import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface GlobeDestinationPreviewProps {
  imageUrl: string;
  name: string;
  country: string;
  skillLevel: string;
  skillLabel?: string;
  onClick?: () => void;
  className?: string;
}

// Mirrors the categorical palette used on the destinations grid (--chart
// tokens, not raw colours) so skill level reads as a category, not a
// stoplight warning. Kept local rather than imported -- this component has
// no other dependency on the grid card, and the mapping is four lines.
const SKILL_BADGE_CLASS: Record<string, string> = {
  beginner: "bg-chart-1",
  intermediate: "bg-chart-4",
  advanced: "bg-chart-5",
  expert: "bg-chart-2",
};

const capitalize = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * Compact horizontal preview for the homepage globe's mobile destination
 * selection -- deliberately not a scaled-down SurfSpotCard. That card's
 * image plus six boxed stats reads as a miniature desktop card once
 * narrowed to mobile width; this is composed like a native list row
 * (thumbnail, title block, chevron) for a quick glance directly beneath
 * the globe, with full stats one tap away on the destination page.
 */
export function GlobeDestinationPreview({
  imageUrl,
  name,
  country,
  skillLevel,
  skillLabel,
  onClick,
  className,
}: GlobeDestinationPreviewProps) {
  const displayLabel = skillLabel ?? capitalize(skillLevel);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group flex w-full items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5 text-left shadow-sm transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className,
      )}
    >
      <img
        src={imageUrl}
        alt={name}
        className="h-16 w-16 shrink-0 rounded-lg object-cover"
        loading="lazy"
      />
      <div className="min-w-0 flex-1">
        <h3 className="truncate font-serif text-sm font-semibold leading-tight text-foreground">{name}</h3>
        <p className="truncate text-xs text-muted-foreground">{country}</p>
        <span
          className={cn(
            "mt-1 inline-block max-w-full truncate rounded-full px-2 py-0.5 text-[10px] font-semibold text-background",
            SKILL_BADGE_CLASS[skillLevel] || "bg-primary",
          )}
        >
          {displayLabel}
        </span>
      </div>
      <div className="flex shrink-0 flex-col items-center gap-0.5 text-primary">
        <span className="text-[10px] font-medium leading-none">View</span>
        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden />
      </div>
    </button>
  );
}
